import type { Identifier } from "ra-core";

import type { AppRole, BusinessArm } from "../../types";
import {
  canAccessCarousel,
  canEditProject,
  isCarouselResource,
} from "./canAccessCarousel";
import {
  CONTACT_EXPEDIENTE_RESOURCE,
  canAccessContactExpediente,
} from "./canAccessContactExpediente";

// FIXME: This should be exported from the ra-core package
type CanAccessParams<
  RecordType extends Record<string, any> = Record<string, any>,
> = {
  action: string;
  resource: string;
  record?: RecordType;
};

/**
 * The current user, as `canAccess` needs it for record-level checks (Almikia
 * Epic E3b, TASK-063): a role alone tells us WHAT a role may do in general,
 * `id`/`business_arm` are needed to tell whether a lead_manager's own scope
 * reaches a given `sales` row. Both auth providers derive this once from the
 * already-cached current sale (see `getActorFromSale`), so no extra request
 * is made per check (see the ticket's `performance` non-functional requirement).
 *
 * Plain `AppRole` strings remain accepted (see `canAccess`) so every
 * pre-existing call site (and most tests) is unaffected -- only the `sales`
 * resource's record-level scoping actually needs `id`/`business_arm`.
 */
export interface Actor {
  role: AppRole;
  id?: Identifier;
  business_arm?: BusinessArm | null;
  /**
   * Almikia Epic EL2 (TASK-073): the projects where she is an active member / Team Leader, from
   * the live `sales_projects` (see `getProjectScope`). Only the BPO carousel gates read them;
   * absent means "unknown" and those gates fail closed.
   */
  memberProjectIds?: Identifier[];
  ledProjectIds?: Identifier[];
  /** Sales ids of the active members of the projects she leads (`shares_led_project`). */
  ledTeamSalesIds?: Identifier[];
  /**
   * Almikia Epic EW3: the per-person permission for the WhatsApp 'Sin clasificar' tray and the
   * personal-numbers list. Absent means no access.
   */
  can_classify_whatsapp_contacts?: boolean;
}

/** A `sales` row reduced to the fields the lead_manager scope rules need. */
export interface ScopedSale {
  id: Identifier;
  role?: AppRole;
  business_arm?: BusinessArm | null;
}

// Resources holding commercial data (leads, clients, pipeline)
const COMMERCIAL_RESOURCES = [
  "contacts",
  "companies",
  "deals",
  "contact_notes",
  "deal_notes",
  "tasks",
];

// Resources only Direccion can delete (the database enforces it too)
const DIRECCION_ONLY_DELETE = [
  "contacts",
  "companies",
  "deals",
  "contact_notes",
  "deal_notes",
];

// Back-office roles get their own modules later; no commercial data for now
const BACK_OFFICE_ROLES: AppRole[] = [
  "administracion",
  "compliance",
  "contabilidad",
];

// Reference data (developers, projects, units): every role reads it, only
// direccion maintains it, nobody deletes it (mirrors the database RLS, TASK-001)
const CATALOGUE_RESOURCES = ["developers", "projects", "units"];

// Clients (Almikia E3): commercial roles manage, auditoria reads, back-office
// none, nobody deletes (mirrors the database RLS, TASK-017)
const CLIENTS_RESOURCE = "clients";

// Developer fiscal data (Almikia E1b): direccion and contabilidad only,
// nobody else, nobody deletes (mirrors the database RLS, TASK-027)
const DEVELOPER_FISCAL_RESOURCES = [
  "developer_fiscal_profiles",
  "developer_fiscal_documents",
];

const SALES_TOOLS_READ_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
  "auditoria",
];

// Almikia Epic EQ (TASK-017): quoter resources (mirrors the RLS, TASK-015/016).
const QUOTE_PLAN_TEMPLATES_RESOURCE = "quote_plan_templates";
const QUOTER_LEAD_RESOURCES = ["quotes", "unit_reservations"];
// Almikia Epic EQ2 (TASK-098): the definitive quoter's per-project configuration
// (mirrors the RLS, TASK-097): read by the commercial roles + auditoria, written
// (create/edit = insert/archive, never delete) by direccion only. `quote_details`
// is read-only (every write is a SECURITY DEFINER function). The custom ("a la
// medida") plan is a right, not a table: `quoter_custom_plan` / `create` (batch EQ3,
// TASK-114: held by every quoting role, the quote is created pending approval).
const QUOTER_CONFIG_RESOURCES = [
  "quoter_configs",
  "quoter_models",
  "quoter_price_lists",
  "quoter_model_list_prices",
  "quoter_schemes",
  "quoter_unit_details",
];
/**
 * Almikia batch EQ3 (TASK-113): the plan tables with no direct write path (RPC-only, scoped by
 * project). `quoter_unit_details` is not one of them (direccion only).
 */
export const isQuoterPlanResource = (resource: string | undefined): boolean =>
  QUOTER_CONFIG_RESOURCES.some(
    (name) => name === resource && name !== "quoter_unit_details",
  );
const QUOTE_DETAILS_RESOURCE = "quote_details";
const QUOTER_CUSTOM_PLAN_RESOURCE = "quoter_custom_plan";
const QUOTER_PLAN_APPROVALS_RESOURCE = "quoter_plan_approvals";
const QUOTER_PLAN_APPROVERS_RESOURCE = "quoter_plan_approvers";
const QUOTING_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
];
const QUOTER_READ_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
  "auditoria",
];

// Almikia Epic EC (TASK-030): property options roles (mirrors the RLS, TASK-029).
const PROPERTY_OPTIONS_READ_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
  "administracion",
  "compliance",
  "auditoria",
];
const PROPERTY_OPTIONS_WRITE_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
];

// Almikia Epic E4 (TASK-026): operations roles (mirrors the RLS, TASK-024).
const OPERATIONS_READ_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
  "auditoria",
  "administracion",
  "compliance",
  // TASK-058 (QE4-8): contabilidad reads operations (read-only, see the client name).
  "contabilidad",
];
// The advisor history inherits the operations visibility in the RLS, but contabilidad is
// explicitly excluded from it (TASK-058).
const OPERATION_HISTORY_READ_ROLES: AppRole[] = OPERATIONS_READ_ROLES.filter(
  (role) => role !== "contabilidad",
);
const OPERATIONS_EDIT_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "administracion",
  "compliance",
];

// Almikia Epic E6 (TASK-039): invoicing read roles (mirrors the RLS, TASK-037).
const INVOICING_READ_ROLES: Record<string, AppRole[]> = {
  receivables: ["direccion", "contabilidad", "auditoria"],
  payables: ["direccion", "contabilidad", "auditoria", "asesora", "lider"],
  advisor_commission_levels: ["direccion", "auditoria"],
  advisor_level_overrides: ["direccion", "auditoria"],
  invoice_concept_template_versions: ["direccion", "auditoria"],
};
const INVOICING_ROW_RESOURCES = Object.keys(INVOICING_READ_ROLES);

/**
 * "Pendientes de VoBo" (receivables and payables awaiting Dirección's VoBo):
 * direccion only. Nobody else approves, so nobody else gets the screen.
 */
export const canAccessPendingVobo = (
  actorOrRole: AppRole | Actor | undefined,
): boolean =>
  (typeof actorOrRole === "string" ? actorOrRole : actorOrRole?.role) ===
  "direccion";

/** Administrative columns edited directly (`enforce_operations_column_guard()`). */
export const OPERATION_ADMINISTRATIVE_FIELDS = [
  "administrative_status",
  "operation_type",
  "client_id",
  "sale_value",
  "lead_type",
] as const;
/** Semaphore columns: also editable by compliance. */
export const OPERATION_SEMAPHORE_FIELDS = [
  "semaphore",
  "semaphore_reason",
] as const;
/** Columns only the sign / cancel / change-advisor actions move. */
export const OPERATION_ACTION_ONLY_FIELDS = [
  "commercial_status",
  "signed_on",
  "contract_folio",
  "sales_id",
  "cancelled_at",
  "cancelled_by",
  "cancel_reason",
] as const;

/**
 * Which operation columns `role` may edit directly: direccion, administracion
 * and lead_manager (RLS limits her to her scope) edit the administrative
 * fields and the semaphore; compliance edits only the semaphore; every other
 * role edits nothing. Mirrors `enforce_operations_column_guard()`.
 */
export const getEditableOperationFields = (role: AppRole): string[] => {
  if (
    role === "direccion" ||
    role === "lead_manager" ||
    role === "administracion"
  ) {
    return [...OPERATION_ADMINISTRATIVE_FIELDS, ...OPERATION_SEMAPHORE_FIELDS];
  }
  if (role === "compliance") {
    return [...OPERATION_SEMAPHORE_FIELDS];
  }
  return [];
};

// Almikia Epic EF (TASK-117): the five buyer-profile option catalogues,
// mirroring the database RLS (DATA CONTRACT EF (4)).
const PROFILE_CATALOGUE_RESOURCES = [
  "profile_buyer_types",
  "profile_credit_types",
  "profile_timelines",
  "profile_motivators",
  "profile_objections",
];

// Almikia Epic E2b (TASK-021): the commercial roles (`private.is_commercial()`),
// the only ones that read or register colegas.
export const COLEGAS_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
];

// Almikia Epic E7 (TASK-088): `advisor_goals` write/list roles, mirroring
// TASK-087's RLS matrix -- direccion (handled by the role === "direccion"
// short-circuit below), a lead_manager within her scope and a lider for her
// team's advisor-scope rows; never asesora or a back-office role. The exact
// scope narrowing (lead_manager brazo, lider team) is enforced server-side
// by RLS, same simplification already used for the commercial resources
// above -- this only gates the boolean UI action.
const ADVISOR_GOALS_ROLES: AppRole[] = ["lead_manager", "lider"];

// Almikia Epic EG (TASK-043): roles of `private.can_access_expenses()`.
const EXPENSE_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "administracion",
  "contabilidad",
];
const EXPENSE_MONTHLY_TOTALS_RESOURCE = "expense_monthly_totals";
const EXPENSE_RESOURCES = [
  "expenses",
  "expense_categories",
  EXPENSE_MONTHLY_TOTALS_RESOURCE,
];

const READ_ACTIONS = ["list", "show", "export"];

/** Almikia Epic E9 (TASK-049): the three Meta webhook tables (direccion only). */
const META_LEADS_RESOURCES = [
  "meta_webhook_events",
  "meta_lead_ingestions",
  "meta_form_projects",
];
/** Almikia Epic EM (TASK-067): Meta origin, campaign report and Conversions API resources. */
const META_ATTRIBUTION_RESOURCE = "deal_meta_attribution";
const META_REPORT_RESOURCE = "meta_campaign_report";
const META_CAPI_RESOURCES = [
  "meta_capi_outbox",
  "meta_capi_log",
  "meta_capi_events_summary",
];
const isMetaLeadsResource = (resource: string | undefined): boolean =>
  resource != null && META_LEADS_RESOURCES.includes(resource);

// Export roles (Almikia Epic E3b, owner answer A6 + owner decisions
// 2026-09-28, contract shared with TASK-066/TASK-071): only these three
// roles may export anything; every other role gets list/show but never
// export. This never widens what a role can read, it only narrows what a
// readable role can additionally export.
const EXPORT_ROLES: AppRole[] = ["direccion", "lead_manager", "administracion"];

// Write actions a lider's team-visibility (TASK-059/TASK-065) never extends
// to: RLS accepts a write only when the record's owner is herself (or the
// lead_manager-scope branch, which does not apply to a lider).
const WRITE_ACTIONS = ["create", "edit", "delete"];

/**
 * Derives the role of a sale, falling back on the legacy administrator flag
 * for records cached before roles existed.
 */
export const getSaleRole = (sale: {
  role?: AppRole | null;
  administrator?: boolean;
}): AppRole => sale.role ?? (sale.administrator ? "direccion" : "asesora");

/**
 * Builds the `Actor` both auth providers pass into `canAccess`, from the sale
 * they already fetched/cached for `getIdentity` -- no extra request.
 */
export const getActorFromSale = (sale: {
  id?: Identifier;
  role?: AppRole | null;
  administrator?: boolean;
  business_arm?: BusinessArm | null;
  can_classify_whatsapp_contacts?: boolean;
}): Actor => ({
  role: getSaleRole(sale),
  id: sale.id,
  business_arm: sale.business_arm ?? null,
  ...(sale.can_classify_whatsapp_contacts === true
    ? { can_classify_whatsapp_contacts: true }
    : {}),
});

/**
 * Mirrors `private.in_lead_manager_scope` / `isInLeadManagerScope` in
 * `supabase/functions/users/orgFields.ts` (see ADR-ee2f59a9-TASK-057): full
 * scope when the lead manager's own brazo is 'ambos'/'direccion_general',
 * same-or-'ambos'-brazo users otherwise, and only herself when her own brazo
 * is NULL (fail-closed). Duplicated rather than imported: the edge function
 * lives outside `src/` in a separate Deno runtime/build, so the two copies
 * are kept in lockstep by the shared contract documented in the ADR, not by
 * a shared module. Exported so the lead form's owner picker (TASK-070,
 * `DealOwnerInput.tsx`) reuses this single scope check instead of
 * reimplementing it a third time.
 */
export const isInLeadManagerScope = (
  leadManager: { id?: Identifier; business_arm?: BusinessArm | null },
  target: ScopedSale,
): boolean => {
  const leadManagerArm = leadManager.business_arm ?? null;
  if (leadManagerArm === "ambos" || leadManagerArm === "direccion_general") {
    return true;
  }
  if (leadManagerArm === null) {
    return target.id === leadManager.id;
  }
  return (
    target.business_arm === leadManagerArm || target.business_arm === "ambos"
  );
};

/**
 * Whether `record`'s owner (`sales_id`) is `actor` herself (Almikia Epic E3b,
 * TASK-065): the record-level counterpart of the database's ownership
 * branch -- RLS grants a lider read access to her team's commercial/client
 * rows (TASK-059) but never a write on them, so the UI must gate every write
 * action the same way. A record `create` form filed against a parent (e.g. a
 * note created under a deal/contact, see `NoteCreate.tsx`) passes that
 * parent as `record`; a plain create form with no parent in context passes
 * none, which always passes through here (matches `canAccessSales`'
 * not-loaded-yet branch: the caller re-checks once the record exists).
 */
const isRecordOwnedByActor = (
  actor: Actor,
  record?: Record<string, any>,
): boolean => {
  if (!record) {
    return true;
  }
  return record.sales_id === actor.id;
};

/**
 * Record-level `sales` (team screen) rules for a lead_manager (Almikia Epic
 * E3b, TASK-063): she may list/show the whole roster, but only edit users
 * within her brazo scope, and never a peer lead_manager (ADR-ee2f59a9-TASK-060)
 * nor create/delete anyone. Every other non-direccion role has no access at
 * all (checked by the caller before reaching here).
 */
const canAccessSales = (actor: Actor, params: CanAccessParams): boolean => {
  if (actor.role !== "lead_manager") {
    return false;
  }
  if (READ_ACTIONS.includes(params.action)) {
    return true;
  }
  if (params.action !== "edit") {
    // "create" (she never creates users) and "delete" (nobody but direccion
    // removes a user) both fall here.
    return false;
  }

  const target = params.record as ScopedSale | undefined;
  if (!target) {
    // The record hasn't loaded yet (e.g. the edit page is still fetching
    // it) -- let the page render; the check re-runs once it is loaded.
    return true;
  }
  if (target.role === "lead_manager" && target.id !== actor.id) {
    return false;
  }
  return isInLeadManagerScope(actor, target);
};

/**
 * A `team_roster` row reduced to the fields the confirm/reject authorization
 * rule needs (Almikia Epic E3b-2, TASK-085): its OWN `business_arm`/`role`,
 * i.e. what the claimant's brazo/rol would become on confirm — never the
 * claimant's CURRENT values (those come from `ScopedSale`/`claimant` below).
 */
export interface ScopedRosterClaim {
  business_arm?: BusinessArm | null;
  role?: AppRole | null;
}

/** The three UI-relevant outcomes of {@link getTeamRosterClaimAuthorization}. */
export interface TeamRosterClaimAuthorization {
  /** Whether the "Claves por confirmar" section shows this claim to `actor` at all. */
  visible: boolean;
  /** Whether `actor` may press Confirmar/Rechazar on it right now. */
  canAct: boolean;
  /**
   * Set when `visible` but not `canAct` because the roster row would change
   * the claimant's role, and only direccion may apply a role change.
   */
  roleChangeRequiresDireccion: boolean;
}

/**
 * Mirrors `private.in_lead_manager_scope_arm` (see
 * `adr/ADR-ee2f59a9-TASK-083-roster-claim-and-confirm.md`): whether a roster
 * row's OWN (non-null) `business_arm` -- what a claimant's brazo would
 * become on confirm -- keeps a lead_manager in scope. There is no live
 * target row to read a `business_arm` from yet, so this cannot reuse
 * `isInLeadManagerScope`'s id-keyed self-fallback branch.
 */
const isRosterArmInLeadManagerScope = (
  leadManagerArm: BusinessArm | null,
  rosterArm: BusinessArm,
): boolean => {
  if (leadManagerArm === "ambos" || leadManagerArm === "direccion_general") {
    return true;
  }
  if (leadManagerArm === null) {
    return false;
  }
  return rosterArm === leadManagerArm || rosterArm === "ambos";
};

/**
 * Mirrors `private.can_confirm_or_reject_team_roster_match` (Almikia Epic
 * E3b-2, TASK-083/085): direccion may act on any pending row; a lead_manager
 * only when the claimant is within her CURRENT brazo scope, is not a
 * lead_manager and not herself, and the roster row's OWN brazo keeps her in
 * scope (a `NULL` roster brazo never narrows scope). A row whose roster role
 * differs from the claimant's current role can only be confirmed by
 * direccion -- surfaced to a lead_manager as visible-but-disabled
 * (`roleChangeRequiresDireccion`), never simply hidden, per the ticket's
 * "shown with that reason and no enabled action" requirement. Shared by the
 * "Claves por confirmar" section (visibility + button gating) and both data
 * providers' `confirmTeamRosterMatch`/`rejectTeamRosterMatch` (server-side
 * authority is still the RPC itself; this only mirrors it).
 */
export const getTeamRosterClaimAuthorization = (
  actor: Actor,
  roster: ScopedRosterClaim,
  claimant: ScopedSale | undefined,
): TeamRosterClaimAuthorization => {
  const hidden: TeamRosterClaimAuthorization = {
    visible: false,
    canAct: false,
    roleChangeRequiresDireccion: false,
  };

  if (actor.role === "direccion") {
    return { visible: true, canAct: true, roleChangeRequiresDireccion: false };
  }
  if (actor.role !== "lead_manager" || !claimant) {
    return hidden;
  }

  const inScope =
    claimant.id !== actor.id &&
    claimant.role !== "lead_manager" &&
    isInLeadManagerScope(actor, claimant) &&
    (roster.business_arm == null ||
      isRosterArmInLeadManagerScope(
        actor.business_arm ?? null,
        roster.business_arm,
      ));
  if (!inScope) {
    return hidden;
  }

  const roleChangeRequiresDireccion =
    roster.role != null && roster.role !== claimant.role;
  return {
    visible: true,
    canAct: !roleChangeRequiresDireccion,
    roleChangeRequiresDireccion,
  };
};

export const canAccess = <
  RecordType extends Record<string, any> = Record<string, any>,
>(
  actorOrRole: AppRole | Actor,
  params: CanAccessParams<RecordType>,
): boolean => {
  const actor: Actor =
    typeof actorOrRole === "string" ? { role: actorOrRole } : actorOrRole;
  const role = actor?.role;

  // Almikia Epic EL2 (TASK-073): the BPO carousel section has its own gates (a project's
  // guardias, office hours, 'no disponible' ranges, 'Activa' flag, notices). Ahead of the
  // direccion short-circuit: the database has no delete policy on most of these tables.
  if (isCarouselResource(params.resource)) {
    return canAccessCarousel(actor, params);
  }

  // Almikia Epic EC2b (TASK-124): the contact expediente right (five audiences, never deleted or
  // exported). Ahead of the direccion short-circuit so even direccion cannot delete/export it.
  if (params.resource === CONTACT_EXPEDIENTE_RESOURCE) {
    return canAccessContactExpediente(actor, params);
  }

  // Almikia Epic EL (TASK-009): the advisor's own "Mi disponibilidad" screen
  // is only for the roles that can receive leads. It stays ahead of the
  // direccion short-circuit: nobody else opens it or edits someone's blocks.
  if (params.resource === "advisor_availability") {
    return params.action === "list" && (role === "asesora" || role === "lider");
  }

  // Almikia Epic EL (TASK-010): "Bitacora de asignacion" is open to every role
  // that can receive or hand out leads (what each one sees is decided by RLS and
  // the `lead_assignments_summary` view), and the "Asignar automaticamente"
  // action is direccion / lead_manager only (the engine rejects other callers).
  if (params.resource === "lead_assignment_log") {
    return (
      params.action === "list" &&
      ["direccion", "lead_manager", "asesora", "lider"].includes(role)
    );
  }
  if (params.resource === "lead_auto_assign") {
    return role === "direccion" || role === "lead_manager";
  }

  if (CATALOGUE_RESOURCES.includes(params.resource)) {
    // ASSUMPTION: catalogue records (developers/projects/units) are never
    // hard-deleted, even by direccion — TASK-001 bans hard deletes on these
    // resources, so the UI never offers an action the database would reject.
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  if (params.resource === CLIENTS_RESOURCE) {
    // ASSUMPTION: clients records are never hard-deleted, even by direccion —
    // mirrors the repo-wide hard-delete ban (TASK-001/TASK-017), so the UI
    // never offers an action the database would reject.
    if (params.action === "delete") {
      return false;
    }
    if (BACK_OFFICE_ROLES.includes(role)) {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    // Auditoria reads every client record but never writes
    if (role === "auditoria") {
      return READ_ACTIONS.includes(params.action);
    }
    // Almikia Epic E3b (TASK-065): a lider reads her team's clients (RLS,
    // TASK-059) but never writes on them.
    if (
      role === "lider" &&
      WRITE_ACTIONS.includes(params.action) &&
      !isRecordOwnedByActor(actor, params.record)
    ) {
      return false;
    }
    return true;
  }

  // Almikia Epic EQ (TASK-017), mirroring the RLS: plan templates are read by
  // the commercial roles + auditoria and written by direccion only; quotes and
  // reservations are read by the same roles and acted on (create/edit = the
  // RPCs) only by the lead-write roles -- an asesora/lider only on her OWN lead
  // (`record` is the lead), auditoria never. Back-office has no access. Nothing
  // is ever deleted or exported. Must stay ahead of the direccion short-circuit.
  if (params.resource === QUOTE_PLAN_TEMPLATES_RESOURCE) {
    if (params.action === "list" || params.action === "show") {
      return QUOTER_READ_ROLES.includes(role);
    }
    return (
      (params.action === "create" || params.action === "edit") &&
      role === "direccion"
    );
  }
  if (QUOTER_CONFIG_RESOURCES.includes(params.resource)) {
    if (params.action === "list" || params.action === "show") {
      return QUOTER_READ_ROLES.includes(role);
    }
    // Almikia batch EQ3 (TASK-113): the plan tables are edited (through the RPCs) by direccion,
    // the BPO lead_manager and the lider of THAT project (`record.project_id`; section-level
    // without a record: a lider who leads any project). Fails closed without the project scope.
    if (isQuoterPlanResource(params.resource)) {
      return (
        (params.action === "create" || params.action === "edit") &&
        canEditProject(actor, params.record?.project_id)
      );
    }
    return (
      (params.action === "create" || params.action === "edit") &&
      role === "direccion"
    );
  }
  if (params.resource === QUOTE_DETAILS_RESOURCE) {
    return (
      (params.action === "list" || params.action === "show") &&
      QUOTER_READ_ROLES.includes(role)
    );
  }
  // Almikia batch EQ3 (TASK-114): the custom plan is offered to every quoting role (it is created
  // pending approval); deciding a pending plan (`quoter_plan_approvals`, edit) is offered to the
  // quoting roles and the SQL checks the configured approver (or direccion); the approver
  // configuration (`quoter_plan_approvers`) is direccion only. Nothing is ever deleted.
  if (params.resource === QUOTER_CUSTOM_PLAN_RESOURCE) {
    return params.action === "create" && QUOTING_ROLES.includes(role);
  }
  if (params.resource === QUOTER_PLAN_APPROVALS_RESOURCE) {
    return (
      (params.action === "list" ||
        params.action === "show" ||
        params.action === "edit") &&
      QUOTING_ROLES.includes(role)
    );
  }
  if (params.resource === QUOTER_PLAN_APPROVERS_RESOURCE) {
    return (
      (params.action === "list" ||
        params.action === "show" ||
        params.action === "create" ||
        params.action === "edit") &&
      role === "direccion"
    );
  }
  if (QUOTER_LEAD_RESOURCES.includes(params.resource)) {
    if (!QUOTER_READ_ROLES.includes(role)) {
      return false;
    }
    if (params.action === "list" || params.action === "show") {
      return true;
    }
    if (params.action !== "create" && params.action !== "edit") {
      return false;
    }
    if (role === "auditoria") {
      return false;
    }
    if (
      (role === "asesora" || role === "lider") &&
      !isRecordOwnedByActor(actor, params.record)
    ) {
      return false;
    }
    return true;
  }

  // Almikia Epic E4 (TASK-026), mirroring the operations RLS (TASK-024):
  // asesora/lider/lead_manager (row scope is RLS), direccion, auditoria,
  // administracion and compliance list/show; contabilidad too, read-only
  // (TASK-058, QE4-8), but not the advisor history below. Nobody
  // creates (SECURITY DEFINER functions do) or deletes (cancelled instead).
  // `edit` is the administrative edit only: direccion, administracion and
  // lead_manager (every administrative field) and compliance (the semaphore
  // only, narrowed per field by `getEditableOperationFields`); the advisor
  // history is read-only for everyone. Sign / cancel / change-advisor are
  // data provider actions with their own rules. Must stay ahead of the
  // direccion short-circuit below.
  if (params.resource === "operations") {
    if (READ_ACTIONS.includes(params.action) && params.action !== "export") {
      return OPERATIONS_READ_ROLES.includes(role);
    }
    return params.action === "edit" && OPERATIONS_EDIT_ROLES.includes(role);
  }
  if (params.resource === "operation_advisor_history") {
    return (
      READ_ACTIONS.includes(params.action) &&
      params.action !== "export" &&
      OPERATION_HISTORY_READ_ROLES.includes(role)
    );
  }

  // Almikia Epic E6 (TASK-039), mirroring the invoicing RLS (TASK-037, VISIBILITY
  // rule): receivables are read by direccion, contabilidad and auditoria;
  // payables by those roles and by an asesora / lider (only the rows she is
  // paid: row scope is RLS, narrowed here when a `record` is passed); the
  // commission catalogue by direccion and auditoria. lead_manager,
  // administracion and compliance read none. NOBODY creates, edits or deletes
  // through the API (the RPCs write), not even direccion, so this block must
  // stay ahead of the direccion short-circuit below.
  if (INVOICING_ROW_RESOURCES.includes(params.resource)) {
    if (params.action !== "list" && params.action !== "show") {
      return false;
    }
    if (!INVOICING_READ_ROLES[params.resource].includes(role)) {
      return false;
    }
    const payee = params.record?.payee_sales_id;
    return !(
      params.resource === "payables" &&
      (role === "asesora" || role === "lider") &&
      payee != null &&
      String(payee) !== String(actor.id)
    );
  }

  // Almikia Epic EC (TASK-030), mirroring the property options RLS (TASK-029):
  // every role but contabilidad reads the options; direccion, lead_manager,
  // asesora and lider create/edit (row ownership is RLS and the guard
  // trigger); nobody deletes or exports (an option is withdrawn). The
  // owner's contact is gated per row by the screens, not here. Must stay
  // ahead of the direccion short-circuit below.
  if (params.resource === "property_options") {
    if (params.action === "list" || params.action === "show") {
      return PROPERTY_OPTIONS_READ_ROLES.includes(role);
    }
    return (
      (params.action === "create" || params.action === "edit") &&
      PROPERTY_OPTIONS_WRITE_ROLES.includes(role)
    );
  }

  // Almikia Epic EC2d (TASK-136), mirroring TASK-135: the Inventario view is read-only for every
  // role that reads the options (nothing else, direccion included); the property types
  // catalogue is listed by those roles, created and edited by direccion only, and archived
  // rather than deleted. Must stay ahead of the direccion short-circuit below.
  if (params.resource === "inventory_items") {
    return (
      (params.action === "list" || params.action === "show") &&
      PROPERTY_OPTIONS_READ_ROLES.includes(role)
    );
  }
  if (params.resource === "property_types") {
    if (params.action === "list" || params.action === "show") {
      return PROPERTY_OPTIONS_READ_ROLES.includes(role);
    }
    return (
      (params.action === "create" || params.action === "edit") &&
      role === "direccion"
    );
  }

  // Almikia Epic EG (TASK-043), mirroring the expenses RLS (TASK-042): the four
  // expense roles read, create and edit expenses (company-wide, no brazo) and
  // export them through `export_expenses()`; every other role is denied every
  // action. Nobody deletes an expense (cancel is a custom method). The category
  // catalogue is written by direccion only and has no export RPC. Must stay
  // ahead of the direccion short-circuit below.
  if (EXPENSE_RESOURCES.includes(params.resource)) {
    if (!EXPENSE_ROLES.includes(role)) {
      return false;
    }
    if (params.resource === EXPENSE_MONTHLY_TOTALS_RESOURCE) {
      return params.action === "list" || params.action === "show";
    }
    if (params.action === "list" || params.action === "show") {
      return true;
    }
    if (params.resource === "expense_categories") {
      return (
        (params.action === "create" || params.action === "edit") &&
        role === "direccion"
      );
    }
    return (
      params.action === "create" ||
      params.action === "edit" ||
      params.action === "export"
    );
  }

  // Almikia Epic EH: sales tools are archived, never deleted (even by
  // direccion); reads for the commercial roles + auditoria, writes for
  // direccion and lead_manager, back-office none (mirrors the RLS, TASK-011).
  // Must stay ahead of the direccion short-circuit below.
  if (params.resource === "sales_tools") {
    if (params.action === "list" || params.action === "show") {
      return SALES_TOOLS_READ_ROLES.includes(role);
    }
    if (params.action === "create" || params.action === "edit") {
      return role === "direccion" || role === "lead_manager";
    }
    return false;
  }

  if (DEVELOPER_FISCAL_RESOURCES.includes(params.resource)) {
    // ASSUMPTION: developer fiscal records are never hard-deleted, even by
    // direccion — mirrors the repo-wide hard-delete ban (TASK-001/TASK-027),
    // so the UI never offers an action the database would reject.
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return role === "direccion";
    }
    return role === "direccion" || role === "contabilidad";
  }

  // Almikia Epic EO (TASK-097), mirroring DATA CONTRACT EO (5): both
  // `org_chart_people` and `culture_documents_current` are computed,
  // read-only views (no INSERT/UPDATE/DELETE policy at all on the SQL side,
  // TASK-096) -- every role lists/shows them, nobody writes through the UI,
  // even direccion. Must stay ahead of the direccion short-circuit below.
  if (
    params.resource === "org_chart_people" ||
    params.resource === "culture_documents_current"
  ) {
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    return READ_ACTIONS.includes(params.action);
  }

  // Almikia Epic EO (TASK-097), mirroring DATA CONTRACT EO (2)/(3)/(5):
  // append-only culture content history -- every role lists/shows it, only
  // direccion creates a new version, nobody (not even direccion) edits or
  // deletes an existing one (a trigger rejects that server-side too).
  if (params.resource === "culture_document_versions") {
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (params.action === "create") {
      return role === "direccion";
    }
    return READ_ACTIONS.includes(params.action);
  }

  // Almikia Epic EO (TASK-097), mirroring DATA CONTRACT EO (2)/(5): a
  // culture document's stable identity -- only direccion creates a new one,
  // nobody edits or deletes one (content changes go through
  // culture_document_versions instead).
  if (params.resource === "culture_documents") {
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (params.action === "create") {
      return role === "direccion";
    }
    return READ_ACTIONS.includes(params.action);
  }

  // Almikia Epic PL (TASK-061), mirroring TASK-060's RLS: every authenticated
  // role creates a feedback report (insert-only, the author can never read it
  // back); only direccion lists, shows and edits (status / note) reports and
  // their summary view; nobody deletes or exports. Must stay ahead of the
  // direccion short-circuit below.
  if (
    params.resource === "feedback_reports" ||
    params.resource === "feedback_reports_summary"
  ) {
    if (params.action === "create") {
      return params.resource === "feedback_reports";
    }
    return (
      role === "direccion" &&
      (params.action === "list" ||
        params.action === "show" ||
        params.action === "edit")
    );
  }

  // Almikia Epic E7 (TASK-088), mirroring TASK-087's RLS matrix: every
  // authenticated role lists/shows the incentive catalogue (read-only
  // reference data, like `developers`/`projects`), only direccion
  // creates/edits a program, and it is never hard-deleted -- even for
  // direccion, an incentive is ended by setting `valid_to`, never a DELETE
  // call, so this must stay ahead of the direccion short-circuit below.
  if (params.resource === "incentive_programs") {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  // Almikia Epic E7 (TASK-088), mirroring TASK-087's RLS matrix: direccion,
  // a lead_manager and a lider list/create/edit goals within their scope;
  // asesora and every back-office role get none. A goal is never
  // hard-deleted -- even for direccion, it's cleared by setting both
  // targets to NULL through "edit", never a DELETE call, so this must stay
  // ahead of the direccion short-circuit below.
  if (params.resource === "advisor_goals") {
    if (params.action === "delete") {
      return false;
    }
    // Export is narrower than list/create/edit: it must never exceed what
    // EXPORT_ROLES already grants elsewhere (the E3b owner rule), so a
    // `lider` -- who gets list/create/edit above -- is intersected out here
    // (RETRY_FEEDBACK: was granting her export too). `administracion` is in
    // EXPORT_ROLES but has no read access to goals at all, so it is also
    // excluded by this intersection.
    if (params.action === "export") {
      return (
        EXPORT_ROLES.includes(role) &&
        (role === "direccion" || ADVISOR_GOALS_ROLES.includes(role))
      );
    }
    return role === "direccion" || ADVISOR_GOALS_ROLES.includes(role);
  }

  // Almikia Epic EP (TASK-102), mirroring DATA CONTRACT EP (6): both
  // `deal_stage_configs` and `deal_stage_criteria` list/show for all 8
  // roles, create/edit for direccion only, delete for nobody -- a stage is
  // archived (never deleted) and a criterion is detached with
  // `enabled: false` (never deleted). Must stay ahead of the direccion
  // short-circuit below, since direccion never gets delete either.
  if (
    params.resource === "deal_stage_configs" ||
    params.resource === "deal_stage_criteria"
  ) {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  // Almikia Epic EA (TASK-110), mirroring DATA CONTRACT EA (8): the activity
  // catalogue (`deal_activity_types`/`deal_activity_results`) and its
  // optional stage link (`deal_stage_activities`) -- list/show for all 8
  // roles, create/edit for direccion only, delete for nobody (an activity or
  // result is archived, a stage link is disabled, never deleted). Must stay
  // ahead of the direccion short-circuit below, since direccion never gets
  // delete either.
  if (
    params.resource === "deal_activity_types" ||
    params.resource === "deal_activity_results" ||
    params.resource === "deal_stage_activities"
  ) {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  // Almikia Epic EF (TASK-117), mirroring DATA CONTRACT EF (4): the five
  // buyer-profile option catalogues (`profile_buyer_types`/
  // `profile_credit_types`/`profile_timelines`/`profile_motivators`/
  // `profile_objections`) -- list/show for all 8 roles, create/edit for
  // direccion only, delete for nobody (an option is archived, never
  // deleted). Must stay ahead of the direccion short-circuit below, since
  // direccion never gets delete either.
  if (PROFILE_CATALOGUE_RESOURCES.includes(params.resource)) {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  // Almikia Epic EF (TASK-117), mirroring DATA CONTRACT EF (4): a lead's
  // buyer profile -- list/show exactly as `deals`; create/edit with the
  // parent lead as `record` exactly as `canAccess('deal_notes', 'create',
  // lead)` (the gate `DealShow` already uses for "Registrar actividad");
  // delete for nobody, ever -- a profile is cleared field-by-field, never
  // deleted. Must stay ahead of the direccion short-circuit below, since
  // direccion never gets delete either.
  if (params.resource === "deal_profiles") {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (params.action === "create" || params.action === "edit") {
      return canAccess(actor, {
        action: "create",
        resource: "deal_notes",
        record: params.record,
      });
    }
    return canAccess(actor, { action: params.action, resource: "deals" });
  }

  // Almikia Epic E9 (TASK-049), mirroring TASK-046's RLS: the Meta webhook log tables are
  // read-only and the form -> project mapping is editable (never deleted); direccion only,
  // every other role gets nothing. Ahead of the direccion short-circuit (no delete for anyone).
  if (isMetaLeadsResource(params.resource)) {
    if (role !== "direccion" || params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return false;
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return params.resource === "meta_form_projects";
  }

  // Almikia Epic EM (TASK-067), mirroring TASK-064/065's RLS and RPC gates. The Meta origin of a
  // lead is readable by exactly the roles that read the lead (read-only, never exported); the
  // campaign report and its CSV are direccion / lead_manager only; the Conversions API outbox
  // and log ("Eventos Meta") are direccion only, where "edit" is the manual retry.
  if (params.resource === META_ATTRIBUTION_RESOURCE) {
    return (
      Boolean(role) &&
      (params.action === "list" || params.action === "show") &&
      canAccess(actor, { action: params.action, resource: "deals" })
    );
  }
  if (params.resource === META_REPORT_RESOURCE) {
    return (
      READ_ACTIONS.includes(params.action) &&
      (role === "direccion" || role === "lead_manager")
    );
  }
  if (META_CAPI_RESOURCES.includes(params.resource)) {
    if (role !== "direccion") {
      return false;
    }
    return (
      (READ_ACTIONS.includes(params.action) && params.action !== "export") ||
      (params.action === "edit" && params.resource === "meta_capi_outbox")
    );
  }

  // Almikia Epic E2b (TASK-021), mirroring TASK-020's RLS: `lead_sources` --
  // list/show for all 8 roles, create/edit for direccion only, delete for
  // nobody (a source is deactivated). Ahead of the direccion short-circuit
  // because direccion never gets delete either.
  if (params.resource === "lead_sources") {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  // Almikia Epic E2b (TASK-021): `colegas` hold a third-party phone, so every
  // action needs a commercial role (auditoria and back-office get nothing);
  // create for all four, edit for direccion/lead_manager, delete for nobody.
  if (params.resource === "colegas") {
    if (params.action === "delete" || !COLEGAS_ROLES.includes(role)) {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (params.action === "edit") {
      return role === "direccion" || role === "lead_manager";
    }
    return true;
  }

  // Almikia Epic EC2a, mirroring the RLS of `supplier_categories` (Proveedor dropdown): read by
  // the commercial roles and administracion, create by the commercial roles (the "Crear
  // categoria" option), edit (rename / deactivate) by direccion and lead_manager, delete for
  // nobody. Ahead of the direccion short-circuit because direccion never gets delete either.
  if (params.resource === "supplier_categories") {
    if (params.action === "delete" || params.action === "export") {
      return false;
    }
    if (READ_ACTIONS.includes(params.action)) {
      return COLEGAS_ROLES.includes(role) || role === "administracion";
    }
    if (params.action === "edit") {
      return role === "direccion" || role === "lead_manager";
    }
    return COLEGAS_ROLES.includes(role);
  }

  // Almikia Epic EC2a, mirroring `tags_write`: every role reads the tags catalogue, only
  // direccion creates, renames or deletes a tag (the former lead_manager write right is gone).
  // Applying a tag to a contact is a write on the contact, not on this catalogue.
  if (params.resource === "tags" && WRITE_ACTIONS.includes(params.action)) {
    return role === "direccion";
  }

  // Almikia Epic ER (TASK-003), mirroring the RLS: the alert-rule catalogue -- list/show for
  // all 8 roles, edit for direccion only, create/delete for NOBODY including direccion (the
  // 4 seeded rows are the whole catalogue for the lifetime of this epic, no insert/delete RLS
  // policy exists at all). Must stay ahead of the direccion short-circuit below, since
  // direccion never gets create/delete either.
  if (params.resource === "alert_rules") {
    if (params.action === "delete" || params.action === "create") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    if (READ_ACTIONS.includes(params.action)) {
      return true;
    }
    return role === "direccion";
  }

  // Almikia Epic ER (TASK-003), mirroring the RLS: lead_alerts -- read-only for everyone, no
  // create/edit/delete for anyone including direccion (only the TASK-005 engine writes,
  // SECURITY DEFINER). Back-office roles (administracion/compliance/contabilidad) get zero
  // rows, same as they get on `deals` itself; RLS additionally narrows a commercial role's
  // rows to her own leads (a Lider BPO never sees her team's alerts, only her own -- ASSUMPTION,
  // see docs/almikia/QUESTIONS.md). Must stay ahead of the direccion short-circuit below, since
  // direccion never gets create/edit/delete either (there is nothing to edit).
  if (params.resource === "lead_alerts") {
    if (WRITE_ACTIONS.includes(params.action)) {
      return false;
    }
    if (params.action === "export") {
      return false;
    }
    return (
      READ_ACTIONS.includes(params.action) && !BACK_OFFICE_ROLES.includes(role)
    );
  }

  // Almikia Epic EW2 (TASK-080): the WhatsApp Inbox screen is for direccion and lead_manager (her
  // rows are narrowed by RLS); an asesora has no Inbox, she sees the badges on her own leads. It is
  // read-only: conversations are never written from the browser. Ahead of the direccion
  // short-circuit so nobody gets a write on it.
  if (
    params.resource === "whatsapp_inbox" ||
    params.resource === "whatsapp_conversations"
  ) {
    return (
      READ_ACTIONS.includes(params.action) &&
      (role === "direccion" || role === "lead_manager")
    );
  }

  // Almikia Epic EW3 (TASK-095): the 'Sin clasificar' tray and the personal-numbers list belong to
  // the holder of the per-person permission ONLY (RLS agrees): not direccion in general, not
  // lideres, not asesoras. Ahead of the direccion short-circuit. An identity without the field
  // (or without a role) gets nothing. Never delete or export.
  if (
    params.resource === "whatsapp_unclassified" ||
    params.resource === "whatsapp_personal_numbers"
  ) {
    return (
      actor?.can_classify_whatsapp_contacts === true &&
      params.action !== "delete" &&
      params.action !== "export"
    );
  }

  // The WhatsApp settings screen, `whatsapp_config` and the template catalogue are direccion-only
  // (RLS of TASK-076 agrees). A template is archived, never deleted: no role gets "delete".
  if (
    params.resource === "whatsapp_settings" ||
    params.resource === "whatsapp_config" ||
    params.resource === "whatsapp_templates"
  ) {
    return role === "direccion" && params.action !== "delete";
  }

  // Almikia Epic EN (TASK-091): the "Avisos internos" settings screen is direccion-only (the RLS
  // and RPC gates of TASK-087 agree). No role gets "delete"; an identity without a role gets
  // nothing.
  if (params.resource === "internal_notices_settings") {
    return role === "direccion" && params.action !== "delete";
  }

  // Direccion has full access
  if (role === "direccion") {
    return true;
  }

  // Configuration and the AI usage screen / its tables (Almikia Epic EI,
  // TASK-005; RLS on ai_settings / ai_usage_log is direccion-only too) stay
  // direccion-only
  if (
    params.resource === "configuration" ||
    params.resource === "ai_usage" ||
    params.resource === "ai_usage_log" ||
    params.resource === "ai_settings" ||
    // Almikia Epic EL (TASK-008): the "Asignación de leads" screen and its tables
    // (RLS writes are direccion-only).
    params.resource === "lead_assignment" ||
    params.resource === "lead_assignment_rules" ||
    params.resource === "lead_assignment_eligible_advisors" ||
    params.resource === "lead_assignment_channels"
  ) {
    return false;
  }

  // The team screen (Almikia Epic E3b, TASK-063): direccion has full access
  // (handled above); a lead_manager gets scoped list/show/edit; every other
  // role has none.
  if (params.resource === "sales") {
    return canAccessSales(actor, params);
  }

  // The "Claves por confirmar" section (Almikia Epic E3b-2, TASK-085):
  // direccion has full access (handled above); a lead_manager only lists it
  // (per-row visibility/actions are then narrowed by
  // `getTeamRosterClaimAuthorization`); every other role has none -- the
  // confirm/reject RPCs refuse them anyway.
  if (params.resource === "team_roster") {
    return role === "lead_manager" && params.action === "list";
  }

  // The LIVE advisor <-> project membership table (Almikia Epic E3b-2,
  // TASK-086): direccion has full access (handled above); every other role
  // reads it -- RLS reads `true` for every authenticated role, exactly like
  // `sales` itself (see adr/ADR-ee2f59a9-TASK-079-team-roster-and-sales-projects.md),
  // so the team list's project column renders for everyone -- but never
  // writes: adding, ending or relabelling a membership is direccion-only
  // (TASK-079's own decision record). A membership is never hard-deleted (it
  // ends by setting `ended_at` through "edit"), so "delete" is always denied.
  if (params.resource === "sales_projects") {
    if (params.action === "delete") {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    return READ_ACTIONS.includes(params.action);
  }

  if (COMMERCIAL_RESOURCES.includes(params.resource)) {
    if (BACK_OFFICE_ROLES.includes(role)) {
      return false;
    }
    if (params.action === "export") {
      return EXPORT_ROLES.includes(role);
    }
    // Auditoria reads every commercial record but never writes
    if (role === "auditoria") {
      return READ_ACTIONS.includes(params.action);
    }
    // Almikia Epic E3b (TASK-065): a lider reads her team's leads, contacts
    // and notes (RLS, TASK-059) but never writes on them -- a note create
    // form passes its parent deal/contact as `record`, so this also blocks
    // creating a note against a team member's record.
    // Almikia Epic EC2a: the one exception is CREATING a contact -- `contacts_insert` lets a lider
    // assign it to herself or to a member of her team (the team itself is resolved by RLS).
    const isLiderCreatingContact =
      role === "lider" &&
      params.resource === "contacts" &&
      params.action === "create";
    if (
      role === "lider" &&
      WRITE_ACTIONS.includes(params.action) &&
      !isLiderCreatingContact &&
      !isRecordOwnedByActor(actor, params.record)
    ) {
      return false;
    }
    if (
      params.action === "delete" &&
      DIRECCION_ONLY_DELETE.includes(params.resource)
    ) {
      return false;
    }
  }

  // Default: every resource not covered by a dedicated block above (e.g.
  // tags) still restricts export to the export roles, whatever else it
  // allows (Almikia Epic E3b, owner answer A6).
  if (params.action === "export") {
    return EXPORT_ROLES.includes(role);
  }

  return true;
};
